export type LeaderboardBoard = "raid" | "tower" | "wealth";

export const LEADERBOARD_BOARDS: LeaderboardBoard[] = ["raid", "tower", "wealth"];

export const LEADERBOARD_BOARD_LABELS: Record<LeaderboardBoard, string> = {
  raid: "คะแนนต่อสู้",
  tower: "หอคอย",
  wealth: "รำรวย",
};

/** Rank badge spritesheet: 2 frames in one row. */
export const RANK_BADGE_SHEET_WIDTH = 266;
export const RANK_BADGE_SHEET_HEIGHT = 32;
export const RANK_BADGE_FRAME_COUNT = 2;
export const RANK_BADGE_FRAME_WIDTH = RANK_BADGE_SHEET_WIDTH / RANK_BADGE_FRAME_COUNT;
export const RANK_BADGE_FRAME_HEIGHT = RANK_BADGE_SHEET_HEIGHT;
/** World scale for overhead badge vs source frame (1 = art pixel size). */
export const RANK_BADGE_OVERHEAD_SCALE = 0.5;
/** Fixed world size for one overhead badge (full sheet row, source aspect). */
export const RANK_BADGE_OVERHEAD_DISPLAY_HEIGHT_PX = Math.round(
  RANK_BADGE_SHEET_HEIGHT * RANK_BADGE_OVERHEAD_SCALE,
);
export const RANK_BADGE_OVERHEAD_DISPLAY_WIDTH_PX = Math.round(
  RANK_BADGE_SHEET_WIDTH * RANK_BADGE_OVERHEAD_SCALE,
);
/** Overhead badge width is at least this multiple of the follow sprite width. */
export const RANK_BADGE_OVERHEAD_MIN_CHARACTER_WIDTH_RATIO = 1.28;
/** Overhead badge sprites show for weekly and live ranks in this range. */
export const RANK_BADGE_TOP_RANK = 10;

export type RankBadgeBuff = {
  attack?: number;
  defense?: number;
  dodge?: number;
  hit?: number;
  critChance?: number;
  critMultiplier?: number;
  attackSpeed?: number;
  moveSpeed?: number;
  hpRegenPerSec?: number;
};

export type RankBadgeConfigBoard = {
  board: LeaderboardBoard;
  spritesheetDataUrl?: string;
  frameWidth: number;
  frameHeight: number;
  buff: RankBadgeBuff;
  updatedAt: number;
};

export type WeeklyBadgeGrant = {
  weekKey: string;
  board: LeaderboardBoard;
  rank: number;
  buffSnapshot: RankBadgeBuff;
};

export type UserWeeklyBadges = {
  weekKey: string;
  expiresAt: number;
  grants: WeeklyBadgeGrant[];
};

/** ISO week key (UTC), aligned with Cloud Functions. */
export function isoWeekKeyUtc(date: Date = new Date()): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export function rankBadgeBuffToStatBonus(buff: RankBadgeBuff): Partial<Record<string, number>> {
  const out: Partial<Record<string, number>> = {};
  for (const [k, v] of Object.entries(buff)) {
    if (typeof v === "number" && Number.isFinite(v) && v !== 0) {
      out[k] = v;
    }
  }
  return out;
}

export function mergeWeeklyBadgeBuffs(grants: WeeklyBadgeGrant[]): RankBadgeBuff {
  const merged: RankBadgeBuff = {};
  for (const g of grants) {
    for (const [k, v] of Object.entries(g.buffSnapshot)) {
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      const key = k as keyof RankBadgeBuff;
      merged[key] = (merged[key] ?? 0) + v;
    }
  }
  if (merged.critChance != null) {
    merged.critChance = Math.min(1, Math.max(0, merged.critChance));
  }
  return merged;
}
